export interface SceneLayout { heightPercent: number; xPercent: number; baselinePercent: number; brightness: number }
export const DEFAULT_SCENE_LAYOUT: SceneLayout = { heightPercent: 30, xPercent: 50, baselinePercent: 98, brightness: 1 };
export const SCENE_LAYOUT_RANGES = { heightPercent: [10, 200], xPercent: [-50, 150], baselinePercent: [-50, 200], brightness: [0.7, 1.3] } as const;

/** Conservative 2D framing heuristic, NOT a measurement of scene depth.
 * Wider scenes leave more scenery visible; broad cutouts also get a width budget.
 * Dimensions come from the trimmed person, not the original photo's margins. */
export function automaticSceneLayout(width: number, height: number, personWidth: number, personHeight: number): SceneLayout {
  if (![width, height, personWidth, personHeight].every(n => Number.isSafeInteger(n) && n > 0)) throw new Error("Invalid scene dimensions");
  const aspect = width / height;
  const heightBudget = Math.min(55, Math.max(24, 40 / Math.sqrt(aspect)));
  const widthBudget = Math.min(45, Math.max(20, 35 / Math.sqrt(aspect)));
  const heightFromWidth = width * widthBudget / 100 / (personWidth / personHeight) / height * 100;
  return { ...DEFAULT_SCENE_LAYOUT, heightPercent: Math.max(10, Math.floor(Math.min(heightBudget, heightFromWidth))) };
}
/** Three reusable cutout previews; choosing one still requires an exact server render and confirmation. */
export function sceneLayoutChoices(width: number, height: number, personWidth: number, personHeight: number) {
  const automatic = automaticSceneLayout(width, height, personWidth, personHeight);
  const medium = Math.max(15, automatic.heightPercent);
  return ([
    ["Small", Math.max(10, Math.round(medium * 0.7))],
    ["Medium", medium],
    ["Large", Math.min(90, Math.round(medium * 1.3))],
  ] as const).map(([label, heightPercent]) => ({ label, layout: { ...automatic, heightPercent } }));
}
export function sceneLayout(raw: unknown): SceneLayout {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("Invalid scene layout");
  const obj = raw as Record<string, unknown>;
  const ranges = SCENE_LAYOUT_RANGES;
  const result = {} as SceneLayout;
  for (const key of Object.keys(ranges) as Array<keyof SceneLayout>) {
    const value = obj[key]; const [min, max] = ranges[key];
    if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) throw new Error(`Invalid layout: ${key}`);
    result[key] = value;
  }
  return result;
}
/** Preserve proportions but allow the frame to crop the figure. Saved ordinary placements retain their coordinates. */
export function sceneBox(width: number, height: number, personWidth: number, personHeight: number, input: SceneLayout) {
  if (![width, height, personWidth, personHeight].every(n => Number.isSafeInteger(n) && n > 0)) throw new Error("Invalid scene dimensions");
  const layout = sceneLayout(input);
  const scale = height * layout.heightPercent / 100 / personHeight;
  const w = Math.max(1, Math.floor(personWidth * scale)); const h = Math.max(1, Math.floor(personHeight * scale));
  // When the figure is wider than the canvas, moving right must still move it right.
  const travel = w < width ? width - w : width;
  return { width: w, height: h, left: Math.round((width - w) / 2 + travel * (layout.xPercent - 50) / 100), top: Math.round(height * layout.baselinePercent / 100 - h) };
}
